import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    getInitialMenuFilter,
    verifiedParams,
    brandIdLookup,
    consoleDebug
} from '../util';
import {
    countActiveFilters,
    resetFilters
} from '../utils/menu/filterUtils';
import {
    processFilterSelection,
} from '../utils/filter/filterProcessors';
import { scrollToProductsNav } from '../utils/scrollUtils';

const FilterContext = createContext(null);

export const useFilter = () => {
    const context = useContext(FilterContext);
    if (!context) {
        throw new Error('useFilter must be used within FilterProvider');
    }
    return context;
};

export const FilterProvider = ({
    children,
    menuFilter,
    filterTerms,
    retailerId,
    specialsExist,
    tagSlugs
}) => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Parse search params
    const searchParams = new URLSearchParams(location.search);
    const urlSearchQuery = searchParams.get('search');
    const urlThcMin = searchParams.get('thcMin');


    // Initialize filters from URL and params
    const [selectedFilters, setSelectedFilters] = useState(() => {
        return getInitialMenuFilter(params, filterTerms, menuFilter, urlSearchQuery, urlThcMin);
    });

    // Store URL params in state for access by child components
    const [thcMin] = useState(urlThcMin);
    const [searchQuery] = useState(urlSearchQuery);

    // Mobile filter state
    const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
    const [selectedSpecialId, setSelectedSpecialId] = useState(null);
    

    // Handle filter changes with unified logic
    const handleFilterChange = useCallback((target, filterType) => {
        consoleDebug('FilterContext handleFilterChange:', { target, filterType });

        setSelectedFilters(prevFilters => {
            const newFilters = processFilterSelection(prevFilters, target, filterType, menuFilter);
            consoleDebug('FilterContext new filters:', newFilters);
            
            // Scroll to ProductsNav when filters change (with a small delay for DOM updates)
            setTimeout(() => {
                scrollToProductsNav();
            }, 100);
            
            return newFilters;
        });
    }, [menuFilter]);

    // Handle range filter changes
    const handleRangeChange = useCallback((updatedRange, filterType) => {
        setSelectedFilters(prevFilters => {
            // Scroll to ProductsNav when range filters change (with a small delay for DOM updates)
            setTimeout(() => {
                scrollToProductsNav();
            }, 100);
            
            return {
                ...prevFilters,
                [filterType]: {
                    ...prevFilters[filterType],
                    min: updatedRange.min,
                    max: updatedRange.max
                }
            };
        });
    }, []);

    // Clear all filters
    const clearAllFilters = useCallback(() => {
        setSelectedFilters(resetFilters(menuFilter));
        setSelectedSpecialId(null);
    }, [menuFilter]);

    // Handle "All" button click
    const handleAllClick = useCallback(() => {
        setSelectedFilters(menuFilter);
        setSelectedSpecialId(null);
    }, [menuFilter]);

    // Count active filters
    const activeFilterCount = useMemo(() =>
        countActiveFilters(selectedFilters, menuFilter),
        [selectedFilters, menuFilter]
    );

    // Handle search
    const setSearchFilter = useCallback((searchValue) => {
        setSelectedFilters(prev => ({
            ...prev,
            search: searchValue
        }));
    }, []);

    // Handle mobile filter toggle
    const toggleMobileFilter = useCallback(() => {
        setMobileFilterOpen(prev => !prev);
    }, []);

    const closeMobileFilter = useCallback(() => {
        setMobileFilterOpen(false);
    }, []);

    // Sync body overflow with mobile filter state
    useEffect(() => {
        document.body.style.overflow = mobileFilterOpen ? 'hidden' : '';
        document.body.classList.toggle('lock-scroll', mobileFilterOpen);

        return () => {
            document.body.style.overflow = '';
            document.body.classList.remove('lock-scroll');
        };
    }, [mobileFilterOpen]);


    // Initialize filters from route params
    useEffect(() => {
        let setFilters = location?.state?.setFilters !== false;
        if (!setFilters) return;

        const currentThcMin = new URLSearchParams(location.search).get('thcMin');
        const currentSearchQuery = new URLSearchParams(location.search).get('search');

        if (params?.categoryType) {
            switch (params.categoryType.toLowerCase()) {
                case 'special':
                    if (params?.categoryName) {
                        setSelectedSpecialId(params.categoryName);
                        setSelectedFilters(sf => ({ ...menuFilter, ...sf, specials: true }));
                    }
                    break;

                case 'specials':
                    // First set specials to true
                    let specialsFilters = { ...menuFilter, specials: true };

                    if (params?.categoryName && params?.specialCategoryName) {
                        // Handle specials with category/brand filters
                        if (params.categoryName === 'category') {
                            // Convert the URL format back to the expected category format
                            const categoryName = params.specialCategoryName.toUpperCase().replace(/-/g, '_');
                            specialsFilters.category = categoryName;
                        } else if (params.categoryName === 'brand') {
                            const brandId = brandIdLookup(params.specialCategoryName, filterTerms.brands);
                            if (brandId) {
                                specialsFilters.brandIds = [brandId];
                            }
                        }
                    }

                    setSelectedFilters(specialsFilters);
                    break;

                case 'brand':
                    if (params?.categoryName) {
                        const brandId = brandIdLookup(params.categoryName, filterTerms.brands);
                        if (brandId) {
                            setSelectedFilters({ ...menuFilter, brandIds: [brandId] });
                        } else {
                            navigate(`/shop/${params.locationSlug}/`);
                        }
                    }
                    break;

                case 'tags':
                    if (params?.categoryName) {
                        // Handle tag filters
                        setSelectedFilters({ ...menuFilter, tags: [params.categoryName] });
                    } else {
                        navigate(`/shop/${params.locationSlug}/`);
                    }
                    break;

                case 'effect':
                    if (params?.categoryName) {
                        // Handle effect filters - effects are typically uppercase
                        setSelectedFilters({ ...menuFilter, effects: [params.categoryName.toUpperCase().replace('-', '_')] });
                    } else {
                        navigate(`/shop/${params.locationSlug}/`);
                    }
                    break;

                case 'subcategory':
                    if (params?.categoryName) {
                        // Handle subcategory filters
                        setSelectedFilters({ ...menuFilter, subcategory: params.categoryName.toUpperCase().replace('-', '_') });
                    } else {
                        navigate(`/shop/${params.locationSlug}/`);
                    }
                    break;
                
                case 'terpenes':
                    if (params?.terpeneName) {
                        // Handle subcategory filters
                        setSelectedFilters({ ...menuFilter, terpenes: params.terpeneName.toUpperCase().replace('-', '_') });
                    } else {
                        navigate(`/shop/${params.locationSlug}/`);
                    }
                    break;

                case 'category':
                    if (params?.categoryName) {
                        const [filterType, filterName] = verifiedParams(
                            params.categoryType,
                            params.categoryName,
                            filterTerms
                        );
                        if (filterType && filterName) {
                            setSelectedFilters({ ...menuFilter, [filterType]: filterName });
                        } else {
                            navigate(`/shop/${params.locationSlug}/`);
                        }
                    }
                    break;

                default:
                    // Handle any other category types
                    if (params?.categoryName) {
                        const [filterType, filterName] = verifiedParams(
                            params.categoryType,
                            params.categoryName,
                            filterTerms
                        );
                        if (filterType && filterName) {
                            let newFilters = { ...menuFilter, [filterType]: filterName };
                            if (currentThcMin) {
                                newFilters.potencyThc = { ...(newFilters.potencyThc || {}), min: parseFloat(currentThcMin) };
                            }
                            setSelectedFilters(newFilters);
                        }
                    }
                    break;
            }
        } else if (currentSearchQuery || currentThcMin) {
            // Handle search and THC min from URL params
            setSelectedFilters(getInitialMenuFilter(params, filterTerms, menuFilter, currentSearchQuery, currentThcMin));
        } else if (location.pathname === `/shop/${params.locationSlug}/` || location.pathname === `/shop/${params.locationSlug}`) {
            // Reset to base menu filter when on shop root
            setSelectedFilters(currentFilters => {
                const baseFilters = { ...menuFilter };
                
                // Preserve existing specials filter if it was already set
                if (currentFilters.specials) {
                    baseFilters.specials = true;
                }
                
                return baseFilters;
            });
        }
    }, [params, location, menuFilter, filterTerms, navigate]);

    // Build filter path for navigation
    const buildFilterPath = useCallback((filters) => {
        let path = `/shop/${params.locationSlug}/`;

        if (filters.specials) {
            path += 'specials/';
            if (filters.category) {
                path += `category/${filters.category.toLowerCase().replace(/\s+/g, '-')}/`;
            } else if (filters.brandIds?.length > 0) {
                const brand = filterTerms.brands?.find(b => b.id === filters.brandIds[0]);
                if (brand) {
                    path += `brand/${brand.name.toLowerCase().replace(/\s+/g, '-')}/`;
                }
            }
        } else if (filters.category) {
            path += `category/${filters.category.toLowerCase().replace(/\s+/g, '-')}/`;
        } else if (filters.brandIds?.length > 0) {
            const brand = filterTerms.brands?.find(b => b.id === filters.brandIds[0]);
            if (brand) {
                path += `brand/${brand.name.toLowerCase().replace(/\s+/g, '-')}/`;
            }
        } else if (filters.effects?.length > 0) {
            // Add effect path
            path += `effect/${filters.effects[0].toLowerCase().replace('_', '-')}/`;
        } else if (filters.subcategory) {
            // Add subcategory path
            path += `subcategory/${filters.subcategory.toLowerCase().replace('_', '-')}/`;
        } else if (filters.tags?.length > 0) {
            // Add tags path
            path += `tags/${encodeURIComponent(filters.tags[0])}/`;
        }

        return path;
    }, [params.locationSlug, filterTerms.brands]);

    // Memoize context value
    const contextValue = useMemo(() => ({
        // State
        selectedFilters,
        setSelectedFilters,
        mobileFilterOpen,
        setMobileFilterOpen,
        selectedSpecialId,
        setSelectedSpecialId,
        activeFilterCount,
        hasActiveFilters: activeFilterCount > 0,

        // Handlers
        handleFilterChange,
        handleRangeChange,
        clearAllFilters,
        handleAllClick,
        setSearchFilter,
        toggleMobileFilter,
        closeMobileFilter,
        buildFilterPath,

        // Data
        menuFilter,
        filterTerms,
        retailerId,
        specialsExist,
        tagSlugs,
        thcMin,
        searchQuery,
    }), [
        selectedFilters,
        mobileFilterOpen,
        setMobileFilterOpen,
        selectedSpecialId,
        activeFilterCount,
        handleFilterChange,
        handleRangeChange,
        clearAllFilters,
        handleAllClick,
        setSearchFilter,
        toggleMobileFilter,
        closeMobileFilter,
        buildFilterPath,
        menuFilter,
        filterTerms,
        retailerId,
        specialsExist,
        tagSlugs,
        thcMin,
        searchQuery,
    ]);

    return (
        <FilterContext.Provider value={contextValue}>
            {children}
        </FilterContext.Provider>
    );
};
